import Button from '@atlaskit/button';
import Spinner from '@atlaskit/spinner';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { useAnalytics, DatabaseContainerType } from '@databases/analytics';
import { useLocale } from '@databases/intl';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { useCallbackEffect } from '@databases/react';
import { appStoreUtils } from '@databases/redux/store';
import { AsyncOperationStatus } from '@databases/redux-types';
import {
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdStatus
} from '@databases/utils/redux/databases/selectors';
import { deserializeError } from '@databases/utils/utils';
import DatabaseValueView from '@databases/value/DatabaseValueView';
import React, { lazy, type FunctionComponent, Suspense, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { dynamicImport } from '@canvas/dynamic-import';

import genericErrorIllustration from '../../../assets/generic_error.svg';

const ShrinkingEmptyState = lazy(() =>
  dynamicImport(() => import('../../../common/components/ShrinkingEmptyState'))
);

const EmbedEmptyState = lazy(() =>
  dynamicImport(() => import('./entry/EmbedEmptyState'))
);

const EmbedValueView: FunctionComponent<{
  contentId: string;
  entryId: string;
  fieldId: string;
  onDataLoaded?: () => void;
}> = ({ contentId, entryId, fieldId, onDataLoaded }) => {
  const [databaseExists, entryExists, fieldExists] = appStoreUtils.useEqualSelector(
    () => {
      const database = appStoreUtils.useSelector((state) =>
        selectDatabaseByContentIdOptional(state, { contentId })
      );
      const field =
        database &&
        !isBrokenDatabase(database) &&
        getDatabaseFieldOptional(database, fieldId);
      const entry =
        database &&
        !isBrokenDatabase(database) &&
        getDatabaseEntryOptional(database, entryId);
      return [!!database, !!entry, !!field] as const;
    }
  );

  const intl = useIntl();
  const { formatMessage } = intl;

  const dispatch = appStoreUtils.useDispatch();
  useCallbackEffect(
    useCallback(async () => {
      GlobalPageLoadExperience.mark('database-load-start');
      await dispatch(loadDatabaseByContentId(contentId));
    }, [contentId, dispatch])
  );
  const databaseStatus = appStoreUtils.useSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );

  const client = useAnalytics();
  const locale = useLocale();

  useCallbackEffect(
    useCallback(async () => {
      await client.sendDatabaseContentViewedEvent({
        contentId,
        databaseContainer: DatabaseContainerType.EMBED_VALUE,
        locale
      });
    }, [client, contentId, locale])
  );

  switch (databaseStatus?.status) {
    case AsyncOperationStatus.ERROR: {
      const error = deserializeError(databaseStatus.error);
      const formattedError = {
        name: error.name,
        message: error.message,
        stack: error.stack
      };
      GlobalPageLoadExperience.failure({
        metadata: { error: formattedError },
        force: true
      });
      logError(new Error(`Couldn't load link with error: ${error}`));
      return (
        <Suspense fallback={<Spinner interactionName="load" />}>
          <ShrinkingEmptyState
            header={formatMessage(messages['embed-view.error-heading-text'])}
            description={formatMessage(messages['embed-view.error-description-text'])}
            imageUrl={genericErrorIllustration}
            primaryAction={
              <Button appearance="primary" onClick={() => globalThis.location.reload()}>
                {formatMessage(messages['embed-view.try-again-button-text'])}
              </Button>
            }
            maxImageWidth={300}
            maxImageHeight={250}
          />
        </Suspense>
      );
    }
    case AsyncOperationStatus.SUCCEEDED:
      GlobalPageLoadExperience.mark('database-load-end');
      GlobalPageLoadExperience.success({ force: true });
      onDataLoaded?.();
      break;
    default:
      return null;
  }

  if (!databaseExists) {
    logError(new Error(`Database with contentId: ${contentId} not found.`));
    return (
      <Suspense fallback={<Spinner interactionName="load" />}>
        <EmbedEmptyState />
      </Suspense>
    );
  }

  if (!entryExists) {
    logError(new Error(`Entry Id: ${entryId} not found.`));
    return (
      <Suspense fallback={<Spinner interactionName="load" />}>
        <EmbedEmptyState />
      </Suspense>
    );
  }

  if (!fieldExists) {
    logError(new Error(`Field Id: ${fieldId} not found.`));
    return (
      <Suspense fallback={<Spinner interactionName="load" />}>
        <EmbedEmptyState />
      </Suspense>
    );
  }

  return <DatabaseValueView contentId={contentId} entryId={entryId} fieldId={fieldId} />;
};
export default EmbedValueView;
